import { literalValues } from "@shared/validation/literal-values";
import type { QuoteStatus } from "../schemas/validators/quoteEnums.lib";

export const QUOTE_DEFAULT_EXPIRY_DAYS = 30;
export const QUOTE_DEFAULT_EXPIRY_MS =
  QUOTE_DEFAULT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

export const QUOTE_EXTENSION_DAYS = 14;
export const QUOTE_EXTENSION_MS = QUOTE_EXTENSION_DAYS * 24 * 60 * 60 * 1000;

export const MAX_QUOTE_STATUS_SCAN = 1_000;

export const MAX_QUOTE_SEARCH_SCAN = 1_024;

export const EDITABLE_QUOTE_STATUSES = new Set<QuoteStatus>(["draft"]);

export const STAFF_EDITABLE_QUOTE_STATUSES: ReadonlySet<QuoteStatus> = new Set<QuoteStatus>([
  "draft",
  "pending_review",
  "sent",
  "viewed",
]);

export const QUOTE_WITH_CUSTOMER_STATUSES: ReadonlySet<QuoteStatus> = new Set<QuoteStatus>(["sent", "viewed"]);

export const QUOTE_EDITED_AFTER_SEND_ACTION = "quote_edited_after_send";

export const QUOTE_CLOSED_EDIT_MESSAGE = "This quote is closed and can no longer be edited.";

export const QUOTE_SUPERSEDED_EDIT_MESSAGE = "A newer version replaced this quote. Edit that one instead.";

export const QUOTE_ORDER_EDIT_MESSAGE = "The customer accepted this quote. Change the order instead.";

export const SENT_QUOTE_PARTIES_MESSAGE =
  "The customer already has this quote, so its buyer, vendor and recipient stay the same. Use Revise and resend to change them.";

export const SENT_QUOTE_SEND_MESSAGE =
  "The customer already has this quote. Save the changes, then email it again.";

export const STAFF_ORDER_NEEDS_APPROVAL_MESSAGE =
  "This deal needs quote approval. Submit the quote for approval, then create the order from it.";

export const QUOTE_AWAITING_APPROVAL_EDIT_MESSAGE =
  "This quote is waiting for approval and can't be edited. The approver can request changes to send it back to draft.";

export const QUOTE_SENT_HOLD_WITHDRAW_MESSAGE =
  "The customer already has this quote, so it can't go back to draft. Ask the approver to decide it.";

export const QUOTE_WITHDRAW_NOT_REP_MESSAGE =
  "Only the quote's rep can take it back to draft. Approve or decline it instead.";

export const QUOTE_EXPIRED_SEND_MESSAGE =
  "This quote's expiry date has passed. Set a new expiry date on the draft, then send it.";

export const HELD_QUOTE_EXPIRED_MESSAGE =
  "This quote's expiry date passed while it waited for approval. Request changes so the rep can set a new expiry date, then approve it when it comes back.";

export const QUOTE_EDITED_SINCE_OPENED_MESSAGE =
  "This quote was edited after you opened it. Review the latest version before approving.";

export const QUOTE_RESENDABLE_STATUSES: ReadonlySet<QuoteStatus> = new Set<QuoteStatus>([
  "sent",
  "viewed",
  "accepted",
  "converted",
  "paid",
]);

export const HOUSE_VENDOR_COMPANY_SLUG = "repackify-house-account";
export const HOUSE_VENDOR_COMPANY_NAME = "Repackify";

export const HOUSE_FREIGHT_CARRIER_SLUG = "repackify-house-freight-carrier";
export const HOUSE_FREIGHT_CARRIER_NAME = "Repackify Freight (internal)";

export const QUOTE_EMAIL_SEND_KIND = {
  QUOTE_SENT: "quote_sent",
  QUOTE_RESENT: "quote_resent",
  QUOTE_TEST: "quote_test",
  QUOTE_SHARED: "quote_shared",
  QUOTE_DECLINE_TO_REP: "quote_decline_to_rep",
  QUOTE_HOLD_NOTICE: "quote_hold_notice",
  QUOTE_HOLD_REMINDER: "quote_hold_reminder",
  QUOTE_MARGIN_NOTICE: "quote_margin_notice",
  QUOTE_APPROVAL_MESSAGE: "quote_approval_message",
  QUOTE_ACCEPTANCE_ON_BEHALF: "quote_acceptance_on_behalf",
} as const;

export const QUOTE_EMAIL_SEND_KINDS = literalValues(QUOTE_EMAIL_SEND_KIND);

type QuoteEmailSendKindValue = (typeof QUOTE_EMAIL_SEND_KIND)[keyof typeof QUOTE_EMAIL_SEND_KIND];

export const QUOTE_EMAIL_SENT_TITLES: Record<QuoteEmailSendKindValue, string> = {
  quote_sent: "Quote emailed",
  quote_resent: "Quote emailed again",
  quote_test: "Test email sent",
  quote_shared: "Quote shared by the buyer",
  quote_decline_to_rep: "Decline emailed to the rep",
  quote_hold_notice: "Approval request emailed to accounting",
  quote_hold_reminder: "Approval reminder emailed to accounting",
  quote_margin_notice: "Margin alert emailed to the rep",
  quote_approval_message: "Message emailed",
  quote_acceptance_on_behalf: "Acceptance for the customer emailed to accounting",
};

export const QUOTE_EMAIL_UNDELIVERED_TITLES: Record<QuoteEmailSendKindValue, string> = {
  quote_sent: "Quote not delivered",
  quote_resent: "Quote not delivered",
  quote_test: "Test email not delivered",
  quote_shared: "Shared quote not delivered",
  quote_decline_to_rep: "Decline email to the rep not delivered",
  quote_hold_notice: "Approval request to accounting not delivered",
  quote_hold_reminder: "Approval reminder to accounting not delivered",
  quote_margin_notice: "Margin alert to the rep not delivered",
  quote_approval_message: "Message email not delivered",
  quote_acceptance_on_behalf: "Acceptance for the customer not delivered to accounting",
};

export const QUOTE_PAYMENT_METHOD = {
  CARD: "card",
  US_BANK_ACCOUNT: "us_bank_account",
} as const;

export const QUOTE_CHECKOUT_OUTCOME = {
  PAID: "paid",
  PROCESSING: "processing",
  ACCEPTED: "accepted",
  OPEN: "open",
} as const;

export const QUOTE_CHECKOUT_STARTED_ACTION = "quote_checkout_started";
export const QUOTE_CHECKOUT_EXPIRED_ACTION = "quote_checkout_expired";

export const QUOTE_CHECKOUT_OUTCOMES = literalValues(QUOTE_CHECKOUT_OUTCOME);

export type QuoteCheckoutOutcome = (typeof QUOTE_CHECKOUT_OUTCOMES)[number];

export const REAL_QUOTE_SEND_KINDS = [
  QUOTE_EMAIL_SEND_KIND.QUOTE_SENT,
  QUOTE_EMAIL_SEND_KIND.QUOTE_RESENT,
] as const;

export const QUOTE_SOURCE = {
  ADMIN: "admin",
  VENDOR: "vendor",
  BUYER: "buyer",
  VENDOR_DIRECT: "vendor_direct",
} as const;

export const QUOTE_SOURCES = literalValues(QUOTE_SOURCE);

export const OUTSIDE_DELIVERY_REASON = "Quote delivered outside Repackify";

export const QUOTE_CLAIM_REFUSAL = {
  EMAIL_MISMATCH: "email_mismatch",
  EMAIL_UNVERIFIED: "email_unverified",
  ORGANIZATION: "organization",
  RESERVED_FOR_RECIPIENT: "reserved_for_recipient",
  PREPARED_FOR_OTHER_COMPANY: "prepared_for_other_company",
} as const;

export const QUOTE_CLAIM_REFUSALS = literalValues(QUOTE_CLAIM_REFUSAL);

export type QuoteClaimRefusal = (typeof QUOTE_CLAIM_REFUSALS)[number];

export const NEW_QUOTE_REQUEST_REASON = {
  DECLINED: "declined",
  EXPIRED: "expired",
} as const;

export const NEW_QUOTE_REQUEST_REASONS = literalValues(NEW_QUOTE_REQUEST_REASON);

export type NewQuoteRequestReason = (typeof NEW_QUOTE_REQUEST_REASONS)[number];

export const QUOTE_DECLINER = {
  REPACKIFY: "repackify",
  CUSTOMER: "customer",
  SELLER: "seller",
} as const;

export const QUOTE_DECLINERS = literalValues(QUOTE_DECLINER);

export type QuoteDecliner = (typeof QUOTE_DECLINERS)[number];

export const QUOTE_ADDITIONAL_CONTACTS_MAX = 10;

export const QUOTE_PARTY_CONTACTS_MAX = 10;

export const QUOTE_BUILDER_FREIGHT_HANDLING = {
  INCLUDED: "included",
  CHARGED_TO_BUYER: "charged_to_buyer",
  PICKUP: "pickup",
  VENDOR_DELIVERY: "vendor_delivery",
} as const;

export const QUOTE_BUILDER_FREIGHT_HANDLINGS = literalValues(QUOTE_BUILDER_FREIGHT_HANDLING);

export const QUOTE_PARTY_CONTACT_ROLE = {
  ACCOUNTING: "accounting",
  DOCK: "dock",
  DISPATCH: "dispatch",
} as const;

export const QUOTE_PARTY_CONTACT_ROLES = literalValues(QUOTE_PARTY_CONTACT_ROLE);

export type QuotePartyContactRole = (typeof QUOTE_PARTY_CONTACT_ROLES)[number];

export const QUOTE_PARTY = {
  VENDOR: "vendor",
  CARRIER: "carrier",
} as const;

export type QuoteParty = (typeof QUOTE_PARTY)[keyof typeof QUOTE_PARTY];

export const QUOTE_PARTY_CONTACT_ROLES_BY_PARTY: Readonly<Record<QuoteParty, ReadonlyArray<QuotePartyContactRole>>> = {
  [QUOTE_PARTY.VENDOR]: [QUOTE_PARTY_CONTACT_ROLE.ACCOUNTING, QUOTE_PARTY_CONTACT_ROLE.DOCK],
  [QUOTE_PARTY.CARRIER]: [QUOTE_PARTY_CONTACT_ROLE.DISPATCH],
};

export const QUOTE_CHANGE_KIND = {
  FREIGHT: "freight",
  VENDOR_STOCK: "vendor_stock",
  ITEMS: "items",
  CUSTOMER: "customer",
  OTHER: "other",
} as const;

export const QUOTE_CHANGE_KINDS = literalValues(QUOTE_CHANGE_KIND);

export type QuoteChangeKind = (typeof QUOTE_CHANGE_KINDS)[number];

export const QUOTE_TERMS_OUTCOME = {
  TERMS_APPROVED: "terms_approved",
  CREDIT_APPLICATION: "credit_application",
} as const;

export const QUOTE_TERMS_OUTCOMES = literalValues(QUOTE_TERMS_OUTCOME);

export type QuoteTermsOutcome = (typeof QUOTE_TERMS_OUTCOMES)[number];

export const QUOTE_APPROVAL_MESSAGE_KIND = {
  SUBMITTED: "submitted",
  CHANGES_REQUESTED: "changes_requested",
  REPLY: "reply",
  RESUBMITTED: "resubmitted",
} as const;

export const QUOTE_APPROVAL_MESSAGE_KINDS = literalValues(QUOTE_APPROVAL_MESSAGE_KIND);

export type QuoteApprovalMessageKind = (typeof QUOTE_APPROVAL_MESSAGE_KINDS)[number];

export const MAX_QUOTE_CHANGES = 10;

export const MAX_QUOTE_CHANGE_NOTE_LENGTH = 300;
export const MAX_QUOTE_NOTE_LENGTH = 5000;
export const QUOTE_REVISION_CHAIN_LIMIT = 50;

export const MAX_QUOTE_APPROVAL_MESSAGE_LENGTH = 2000;

export const MAX_QUOTE_DECLINE_COMMENT_LENGTH = 1000;

export const QUOTE_APPROVAL_MESSAGE_LIMIT = 100;

export const QUOTE_BUYER_NAME_FALLBACK = "Buyer";

export const BUYER_QUOTE_LIST_STATE = {
  OPEN: "open",
  IN_REVIEW: "in_review",
  ORDERED: "ordered",
  PAID: "paid",
  EXPIRED: "expired",
  DECLINED: "declined",
  CLOSED: "closed",
} as const;

export const BUYER_QUOTE_LIST_STATES = literalValues(BUYER_QUOTE_LIST_STATE);

export type BuyerQuoteListState = (typeof BUYER_QUOTE_LIST_STATES)[number];

export const MAX_BUYER_QUOTE_LIST = 100;

export const MAX_BUYER_QUOTE_THUMBNAILS = 2;

export const QUOTE_SHARE_ACCESS = {
  CAN_ORDER: "can_order",
  CAN_VIEW: "can_view",
  GETS_INVOICES: "gets_invoices",
  PREPARED_IT: "prepared_it",
} as const;

export const QUOTE_SHARE_ACCESSES = literalValues(QUOTE_SHARE_ACCESS);

export type QuoteShareAccess = (typeof QUOTE_SHARE_ACCESSES)[number];

export const QUOTE_BUILDER_LISTING_READINESS = {
  ALL: "all",
  READY: "ready",
  NEEDS_FINISHING: "needs_finishing",
} as const;

export const QUOTE_BUILDER_LISTING_READINESSES = literalValues(QUOTE_BUILDER_LISTING_READINESS);

export type QuoteBuilderListingReadiness = (typeof QUOTE_BUILDER_LISTING_READINESSES)[number];

export const QUOTE_BUILDER_CANDIDATE_WINDOW = 150;

export const QUOTE_BUILDER_PAGE_DEFAULT = 30;

export const QUOTE_BUILDER_PAGE_MAX = 120;

export const QUOTE_BUILDER_LISTINGS_BY_IDS_MAX = 100;

export const BUYER_DETAIL_GAP = {
  PHONE: "phone",
  ACCOUNTING_CONTACT: "accounting-contact",
  BILLING_ADDRESS: "billing-address",
  DELIVERY_ADDRESS: "delivery-address",
  DELIVERY_DOCK_HOURS: "delivery-dock-hours",
} as const;

export const BUYER_DETAIL_GAPS = literalValues(BUYER_DETAIL_GAP);

export type BuyerDetailGap = (typeof BUYER_DETAIL_GAP)[keyof typeof BUYER_DETAIL_GAP];

export const QUOTE_OUTCOME_REASON = {
  PRICE_TOO_HIGH: "price_too_high",
  FOUND_ELSEWHERE: "found_elsewhere",
  TIMING_NOT_RIGHT: "timing_not_right",
  WRONG_PRODUCT_FIT: "wrong_product_fit",
  BUDGET_CONSTRAINTS: "budget_constraints",
  PROJECT_CANCELLED: "project_cancelled",
  VENDOR_UNAVAILABLE: "vendor_unavailable",
  NO_RESPONSE: "no_response",
  OTHER: "other",
} as const;

export const QUOTE_OUTCOME_REASONS = literalValues(QUOTE_OUTCOME_REASON);

export type QuoteOutcomeReason = (typeof QUOTE_OUTCOME_REASONS)[number];

export const QUOTE_OUTCOME_SYSTEM_CODE = {
  LAPSED_AFTER_CONTACT: "lapsed_after_contact",
  APPROVAL_LAPSED: "approval_lapsed",
  SUPERSEDED: "superseded",
  CLAIMED_BY_OTHER: "claimed_by_other",
  REQUEST_CANCELLED: "request_cancelled",
  ORDER_CANCELLED: "order_cancelled",
  VENDOR_WITHDREW: "vendor_withdrew",
  DRAFT_DISCARDED: "draft_discarded",
  UNRECORDED: "unrecorded",
} as const;

export const QUOTE_OUTCOME_SYSTEM_CODES = literalValues(QUOTE_OUTCOME_SYSTEM_CODE);

export type QuoteOutcomeSystemCode = (typeof QUOTE_OUTCOME_SYSTEM_CODES)[number];

export const QUOTE_OUTCOME_CODES = [...QUOTE_OUTCOME_REASONS, ...QUOTE_OUTCOME_SYSTEM_CODES] as const;

export type QuoteOutcomeCode = QuoteOutcomeReason | QuoteOutcomeSystemCode;

export const QUOTE_OUTCOME_SOURCE = {
  BUYER: "buyer",
  STAFF: "staff",
  VENDOR: "vendor",
  SYSTEM: "system",
  HISTORY: "history",
} as const;

export const QUOTE_OUTCOME_SOURCES = literalValues(QUOTE_OUTCOME_SOURCE);

export type QuoteOutcomeSource = (typeof QUOTE_OUTCOME_SOURCES)[number];

export const QUOTE_PRICE_BASIS = {
  PER_UNIT: "per_unit",
  ORDER_TOTAL: "order_total",
} as const;

export const QUOTE_PRICE_BASES = literalValues(QUOTE_PRICE_BASIS);

export const QUOTE_COMPETITOR_FREIGHT = {
  DELIVERED: "delivered",
  PICKUP: "pickup",
  UNKNOWN: "unknown",
} as const;

export const QUOTE_COMPETITOR_FREIGHT_TERMS = literalValues(QUOTE_COMPETITOR_FREIGHT);

export const QUOTE_OUTCOME_COMMENT_MAX = 1000;

export const QUOTE_EXPIRY_NOTICE_LEAD_MS = 2 * 24 * 60 * 60 * 1000;

export const QUOTE_EXPIRY_NOTICE_MAX_PER_RUN = 200;

export const QUOTE_CONTACT_EVIDENCE_SCAN_LIMIT = 25;
